import type { DOMAttributes, ReactElement, ReactNode } from "react";
import { Focusable, OverlayArrow, Tooltip as RACTooltip, TooltipTrigger, type TooltipProps } from "react-aria-components";
import { Kbd } from "../kbd";
import { cx } from "../tv";

type Props = {
  /** The trigger must be a focusable React Aria element (Button, Link) or is wrapped in Focusable. */
  children: ReactElement;
  content: ReactNode;
  shortcut?: string;
  placement?: TooltipProps["placement"];
  delay?: number;
  /** Set when the trigger is not a React Aria pressable, so it gets wrapped in Focusable. */
  wrap?: boolean;
};

/** 500 ms delay, instant while moving between tooltips (React Aria's warm-up), shortcut slot (DESIGN-RULES 9). */
export function Tooltip({ children, content, shortcut, placement = "top", delay = 500, wrap }: Props) {
  return (
    <TooltipTrigger delay={delay} closeDelay={80}>
      {wrap ? <Focusable>{children as ReactElement<DOMAttributes<HTMLElement>, string>}</Focusable> : children}
      <RACTooltip
        placement={placement}
        offset={8}
        className={({ isEntering, isExiting }) =>
          cx(
            "z-(--z-toast) flex max-w-72 items-center gap-2 rounded-md bg-fg px-2.5 py-1.5 text-xs font-medium text-panel-solid shadow-pop",
            "origin-(--trigger-anchor-point) will-change-transform",
            isEntering && "animate-[os-tip-in_var(--dur-fast)_var(--ease-out)]",
            isExiting && "animate-[os-tip-out_var(--dur-instant)_var(--ease-out)]",
          )
        }
      >
        <OverlayArrow className="group">
          <svg width={8} height={8} viewBox="0 0 8 8" className="fill-fg group-data-[placement=bottom]:rotate-180">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        <span className="truncate-1">{content}</span>
        {shortcut && <Kbd tone="inverse">{shortcut}</Kbd>}
      </RACTooltip>
    </TooltipTrigger>
  );
}
